import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { RMCheckBoxY } from 'components/customComponents/Controls/RMCheckbox';
import { Table } from 'components/main/ProteinFormulation/ProteinIngredients';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { generateSingleDropReportPDF } from './SingleDropReportPDF';
import { LoadingModal } from 'components/modal';
import { RMRadioButton } from 'components/customComponents/Layers/InputGroups';
import {
  useAllWellNavigationDetails,
  useSelectedDropImageParameters,
} from 'redux/hooks/experiment';
import { FmlxBanner } from 'fmlx-common-ui';
import { isDropModeUrl, showAlertSnackBar } from '../../utils/Global';
import { useExperimentReady } from './hooks';
import EAlertType from '../../enums/EAlertType';
import ESnackBarPosition from '../../enums/ESnackBarPosition';
import { useImageZoomVisibleArea } from '../well/hooks';
import { ImageZoomDTO, InspectionSetDTO } from '../../business/models/Models';

type CaptureProfileOption = {
  id: number;
  name?: string;
  selected: boolean;
  isDisabled: boolean;
};

type SingleDroptReportOptionProps = {
  docData: ExperimentDtoClient;
  canvasRef?: any;
  platePadding?: number;
  snfDropData?: any;
};

export type SingleDropReportOptionsDTO = {
  dropNumber: number;
  sortImage: string;
  proteinFormulation: boolean;
  proteinIngredients: boolean;
  standardIngredients: boolean;
  longNameIngredients: boolean;
  wellCondition: boolean;
  longNameIngredientsWell: boolean;
  dropImage: boolean;
  scores: boolean;
  dateImaged: boolean;
  useCurrentZoom: boolean;
  useBestFocusLevel: boolean;
  thumbnail: boolean;
  dropImageHistory: boolean;
  thumbnailHistory: boolean;
  notes: string;
  captureProfiles: CaptureProfileOption[];
  imageZoom: ImageZoomDTO;
  selectedInspection: InspectionSetDTO;
};

function findInspectionByImageBatchId(
  inspections: InspectionSetDTO[],
  imageBatchId: number,
): InspectionSetDTO | undefined {
  return inspections?.find((inspectionSet) =>
    inspectionSet.groupedInspections?.some((gi) => gi.imageBatchId == imageBatchId),
  );
}

export function OptionsRadioButton({ onClick, value, label, selected }: any) {
  return (
    <RMRadioButton
      selected={selected}
      value={value}
      styleLabel={{
        fontSize: '13px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '14.95px',
        letterSpacing: '0em',
        textAlign: 'left',
        marginTop: '0.3rem',
        marginBottom: '0.2rem',
      }}
      styleCircle={{
        height: '1.25rem',
        width: '1.25rem',
        marginRight: '0.75rem',
        display: 'block',
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
      }}
      onClick={(e) => onClick(e)}
      label={label}
    />
  );
}

export const SingleDropReportOptions = forwardRef(
  (props: SingleDroptReportOptionProps, ref) => {
    const { snfDropData, docData } = props;
    const [dropNumber, setDropNumber] = useState(1);
    const [sortImage, setSortImage] = useState('byInspection');
    const [proteinFormulation, setProteinFormulation] = useState(true);
    const [proteinIngredients, setProteinIngredients] = useState(false);
    const [standardIngredients, setStandardIngredients] = useState(false);
    const [longNameIngredients, setLongNameIngredients] = useState(false);
    const [wellCondition, setWellCondition] = useState(true);
    const [longNameIngredientsWell, setLongNameIngredientsWell] = useState(true);
    const [dropImage, setDropImage] = useState(true);
    const [scores, setScores] = useState(true);
    const [dateImaged, setDateImaged] = useState(true);
    const [useCurrentZoom, setUseCurrentZoom] = useState(false);
    const [useBestFocusLevel, setUseBestFocusLevel] = useState(true);
    const [thumbnail, setThumbnail] = useState(false);
    const [dropImageHistory, setDropImageHistory] = useState(true);
    const [thumbnailHistory, setThumbnailHistory] = useState(true);
    const [captureProfiles, setCaptureProfiles] = useState<CaptureProfileOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const notes = 'new notes';
    const [wellDetails, setWellDetails] = useState([]);
    const allWellNavigationDetails = useAllWellNavigationDetails();
    const [isDropSelectionDisabled, setIsDropSelectionDisabled] =
      useState<boolean>(false);
    const selectedDropImageParameters = useSelectedDropImageParameters();
    const completeExperimentData = useExperimentReady(docData);
    const { imageZoomVisibleArea } = useImageZoomVisibleArea();
    const isDropMode = isDropModeUrl();

    const columns = useMemo(
      () => [
        { Header: 'Imaging Setting', accessor: 'name', width: '80%' },
        { Header: 'Selected', accessor: 'value', width: '20%' },
      ],
      [],
    );

    useEffect(() => {
      const newCaptureProfiles = [];
      if (snfDropData) {
        docData.profiles.forEach((profile, i) => {
          newCaptureProfiles.push({
            ...profile,
            selected: snfDropData.captureResultProfileId
              ? profile.id == snfDropData.captureResultProfileId
              : i == 0,
            isDisabled: true,
          });
        });

        setDropNumber(snfDropData.wellDropNumber);
        setIsDropSelectionDisabled(true);

        const wellDetails: any[] = [];
        const snfWellDetails = {} as any;
        snfWellDetails.wellNum = snfDropData.wellNumber;
        snfWellDetails.plateID = snfDropData.plateId;
        wellDetails.push(snfWellDetails);

        setWellDetails(wellDetails);
      } else {
        docData.profiles.forEach((profile, i) => {
          newCaptureProfiles.push({
            ...profile,
            selected: profile.id === selectedDropImageParameters.cpId,
            isDisabled: false,
          });
        });

        setDropNumber(selectedDropImageParameters.dropNumber);
        setWellDetails(allWellNavigationDetails);
      }

      setCaptureProfiles(newCaptureProfiles);
    }, []);

    useImperativeHandle(ref, () => ({
      generateAction,
    }));

    useEffect(() => {
      if (proteinFormulation === false) {
        setProteinIngredients(false);
        setStandardIngredients(false);
        setLongNameIngredients(false);
      }
    }, [proteinFormulation]);

    useEffect(() => {
      if (dropImage === false) {
        setScores(false);
        setDateImaged(false);
        setUseCurrentZoom(false);
        setUseBestFocusLevel(false);
        setThumbnail(false);
      }
    }, [dropImage]);

    useEffect(() => {
      if (dropImageHistory === false) {
        setThumbnailHistory(false);
      }
    }, [dropImageHistory]);

    useEffect(() => {
      if (wellCondition === false) {
        setLongNameIngredientsWell(false);
      }
    }, [wellCondition]);

    async function generateAction(saveReport: boolean, onPopupBlocked?: () => void) {
      setIsLoading(true);
      const imageZoom = imageZoomVisibleArea
        ? new ImageZoomDTO(imageZoomVisibleArea)
        : null;
      const completeDocData = snfDropData ? docData : await completeExperimentData;
      const selectedInspection = snfDropData
        ? findInspectionByImageBatchId(
            completeDocData?.inspections,
            snfDropData.imageBatchId,
          )
        : selectedDropImageParameters.inspection;
      const reportOptions: SingleDropReportOptionsDTO = {
        dropNumber,
        sortImage,
        proteinFormulation,
        proteinIngredients,
        standardIngredients,
        longNameIngredients,
        wellCondition,
        longNameIngredientsWell,
        dropImage,
        scores,
        dateImaged,
        useCurrentZoom,
        useBestFocusLevel,
        thumbnail,
        dropImageHistory,
        thumbnailHistory,
        captureProfiles,
        notes,
        imageZoom: imageZoom,
        selectedInspection,
      };

      if (completeDocData.inspections.length === 0) {
        showAlertSnackBar(
          EAlertType.WARNING,
          ESnackBarPosition.TOP,
          'This plate does not have any inspection.',
        );
        setIsLoading(false);
      } else {
        //**Need to check this */
        if (snfDropData) {
          await generateSingleDropReportPDF(
            reportOptions,
            completeDocData,
            wellDetails,
            saveReport,
            onPopupBlocked,
          );
        } else {
          await generateSingleDropReportPDF(
            reportOptions,
            completeDocData,
            wellDetails,
            saveReport,
            onPopupBlocked,
          );
        }
        setIsLoading(false);
      }
    }

    function captureProfilesChangeHandler(flag: boolean, profile: CaptureProfileOption) {
      captureProfiles.find((p) => p.id == profile.id).selected = flag;
      setCaptureProfiles([...captureProfiles]);
    }

    function setDropNumberValue(e) {
      setDropNumber(e.target.value);
    }
    function sortImageSelectedValue(e) {
      const value = e.target.value;
      setSortImage(value);
      return e;
    }

    return (
      <div
        id="singledropreportoptions-wrapper"
        className="dashed-border d-flex h-100 p-2 flex-column"
        style={{ height: '40rem', border: 'none' }}
      >
        <div
          id="singledropreportoptions-warning"
          style={{ marginBottom: '1rem' }}
          hidden={wellDetails.length <= 96}
        >
          <FmlxBanner
            id="singledropreportoptions-warning-content"
            variant="warning"
            title="This process will take a few seconds depending on network speed."
            hideCloseButton
          />
        </div>
        <div id="singledropreportoptions-confirmation-text" className="text-p mb-2">
          Please provide information you want to include into report
        </div>
        <div id="singledropreportoptions-value-wrapper" className="d-flex flex-row h-90">
          <div
            id="singledropreportoptions-divider-right-value"
            className="flex-50 divider-right p-2 d-flex flex-column"
          >
            <div id="singledropreportoptions-drop" className="row">
              <div
                id="singledropreportoptions-drop-col"
                className="col-md-2 text-p"
                style={{ marginLeft: '10px' }}
              >
                <label id="singledropreportoptions-label-drop">Drop</label>
              </div>
              <div id="singledropreportoptions-drop-number" className="col-md-6 text-p">
                <select
                  name="dropNumber"
                  id="singledropreportoptions-select-dropNumber"
                  disabled={isDropSelectionDisabled}
                  onChange={setDropNumberValue}
                  value={dropNumber}
                >
                  {docData.containerDrops.map((drop, index) => (
                    <option
                      id={`singledropreportoptions-contDrop-${index}`}
                      key={`contDrop_${index}`}
                      value={drop.dropNumber}
                    >
                      {drop.dropNumber}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div id="singledropreportoptions-sort-image-inspection" className="row">
              <div
                id="singledropreportoptions-sort-image-inspection-col"
                className="col-md-12 text-p"
                style={{ marginLeft: '10px' }}
              >
                <label id="singledropreportoptions-label-sort-image-inspection">
                  Sort Image Inspection
                </label>
              </div>
            </div>
            <div id="singledropreportoptions-image-selected-value" className="row">
              <div
                id="single-drop-report-options-image-selected-value-col"
                className="col-md-12"
                style={{ marginLeft: '10px' }}
              >
                <OptionsRadioButton
                  id="single-drop-report-options-radio-button-by-inspection"
                  onClick={sortImageSelectedValue}
                  value="byInspection"
                  label="By Date Inspection"
                  selected={sortImage}
                />
                <OptionsRadioButton
                  id="single-drop-report-options-radio-button-by-capturing-profile"
                  onClick={sortImageSelectedValue}
                  value="byCaptureProfile"
                  label="By Imaging Settings"
                  selected={sortImage}
                />
              </div>
            </div>

            <ReportOption
              input={
                <RMCheckBoxY
                  isChecked={proteinFormulation}
                  onChange={(e) => setProteinFormulation(e)}
                />
              }
              label="Protein Formulation"
              id="ingledropreportoptions-input-protein-formulation"
            />
            <div id="singledropreportoptions-input-ingredients-wrapper" className="ml-4">
              <ReportOption
                input={
                  <RMCheckBoxY
                    disabled={!proteinFormulation}
                    isChecked={proteinIngredients}
                    onChange={(e) => setProteinIngredients(e)}
                  />
                }
                label="Protein Ingredients"
                id="singledropreportoptions-input-protein-ingredients"
              />
              <ReportOption
                input={
                  <RMCheckBoxY
                    disabled={!proteinFormulation}
                    isChecked={standardIngredients}
                    onChange={(e) => setStandardIngredients(e)}
                  />
                }
                label="Standard Ingredients"
                id="singledropreportoptions-input-standard-ingredients"
              />
              <div id="singledropreportoptions-long-name-wrapper" className="ml-4">
                <ReportOption
                  input={
                    <RMCheckBoxY
                      disabled={!(standardIngredients && proteinFormulation)}
                      isChecked={longNameIngredients}
                      onChange={(e) => setLongNameIngredients(e)}
                    />
                  }
                  label="Long Name Ingredients"
                  id="singledropreportoptions-input-long-name-ingredients"
                />
              </div>
            </div>
            <ReportOption
              input={
                <RMCheckBoxY
                  isChecked={wellCondition}
                  onChange={(e) => setWellCondition(e)}
                />
              }
              label="Well Condition"
              id="singledropreportoptions-input-well-condition"
            />
            <div id="singledropreportoptions-well-long-name-wrapper" className="ml-4">
              <ReportOption
                input={
                  <RMCheckBoxY
                    disabled={!wellCondition}
                    isChecked={longNameIngredientsWell}
                    onChange={(e) => setLongNameIngredientsWell(e)}
                  />
                }
                label="Long Name Ingredients"
                id="singledropreportoptions-input-well-long-name-ingredients"
              />
            </div>
          </div>
          <div
            id="singledropreportoptions-divider-right-imaging"
            className="flex-50 divider-right p-2 d-flex flex-column"
          >
            <ReportOption
              input={
                <RMCheckBoxY isChecked={dropImage} onChange={(e) => setDropImage(e)} />
              }
              id="singledropreportoptions-input-drop-image"
              label="Drop Image"
            />
            <div id="singledropreportoptions-input-scores-wrapper" className="ml-4">
              <ReportOption
                input={
                  <RMCheckBoxY
                    disabled={!dropImage}
                    isChecked={scores}
                    onChange={(e) => setScores(e)}
                  />
                }
                id="singledropreportoptions-input-scores"
                label="Scores"
              />
              <ReportOption
                input={
                  <RMCheckBoxY
                    disabled={!dropImage}
                    isChecked={dateImaged}
                    onChange={(e) => setDateImaged(e)}
                  />
                }
                id="singledropreportoptions-input-date-imaged"
                label="Date Imaged"
              />
              <ReportOption
                input={
                  <RMCheckBoxY
                    disabled={!isDropMode || !dropImage}
                    isChecked={useCurrentZoom}
                    onChange={(e) => setUseCurrentZoom(e)}
                  />
                }
                id="singledropreportoptions-input-use-current-zoom"
                label="Use Current Zoom"
              />
              <ReportOption
                input={
                  <RMCheckBoxY
                    disabled={!dropImage}
                    isChecked={useBestFocusLevel}
                    onChange={(e) => setUseBestFocusLevel(e)}
                  />
                }
                id="singledropreportoptions-input-use-best-focus-level"
                label="Use Best Focus Level"
              />
              <ReportOption
                input={
                  <RMCheckBoxY
                    disabled={!dropImage}
                    isChecked={thumbnail}
                    onChange={(e) => setThumbnail(e)}
                  />
                }
                id="singledropreportoptions-input-thumbnail"
                label="Thumbnail"
              />
            </div>
            <ReportOption
              input={
                <RMCheckBoxY
                  isChecked={dropImageHistory}
                  onChange={(e) => setDropImageHistory(e)}
                />
              }
              id="singledropreportoptions-input-drop-image-history"
              label="Drop Image History"
            />
            <div
              id="singledropreportoptions-input-thumbnail-history-wrapper"
              className="ml-4"
            >
              <ReportOption
                input={
                  <RMCheckBoxY
                    disabled={!dropImageHistory}
                    isChecked={thumbnailHistory}
                    onChange={(e) => setThumbnailHistory(e)}
                  />
                }
                id="singledropreportoptions-input-thumbnail-history-wrapper"
                label="Thumbnail"
              />
            </div>
          </div>

          <div id="singledropreportoptions-table-wrapper" className="flex-50 p-2">
            <Table
              columns={columns}
              data={captureProfiles.map((val) => {
                return {
                  name: val.name,
                  value: (
                    <RMCheckBoxY
                      disabled={val.isDisabled}
                      isChecked={val.selected}
                      onChange={(e) => captureProfilesChangeHandler(e, val)}
                    />
                  ),
                };
              })}
            />
          </div>
        </div>
        <LoadingModal showModal={isLoading} text={'Preparing'} />
      </div>
    );
  },
);

const ReportOption = ({ input, label }: any) => {
  return (
    <div
      id="singledropreportoptions-report-option-wrapper"
      className="d-flex flex-row align-items-baseline"
    >
      <div id={`singledropreportoptions-report-option-${input}`}>{input}</div>
      <label
        id={`singledropreportoptions-report-option-${label}`}
        className="flex-90 text-p non-select"
      >
        {' '}
        {label}{' '}
      </label>
    </div>
  );
};
